import { Avatar, Button, Dialog, Menu, MenuItem, Popover } from "@mui/material";
import { useFormik, Field, FormikProvider } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as AddIcon } from "assets/add-icon.svg";
import { ReactComponent as AddUserIcon } from "assets/add-user.svg";
import { ReactComponent as ArrowDown } from "assets/arrow-down.svg";
import { ReactComponent as CrownIcon } from "assets/crown.svg";
import { ReactComponent as FolderAddIcon } from "assets/folder-add.svg";
import { ReactComponent as FolderIcon } from "assets/folder.svg";
import { ReactComponent as SettingsIcon } from "assets/gear.svg";
import { ReactComponent as RenameIcon } from "assets/pencil-icon.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import ProjectItem from "components/ProjectItem";
import { PLANS } from "constants/constants";
import { ProjectDetail } from "models/Project";
import { UserDetail } from "models/User";
import { Workspace } from "models/Workspace";
import { renameWorkspace } from "services/WorkspaceService";

import styles from "./WorkspaceItem.module.scss";
import {
  SLAPSHOT_INVITE_USER,
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_SUBSCRIPTION_URL,
} from "../../constants/urls";
import { createProject } from "../../services/ProjectService";
import { getTextInitials, getUser, getUserNameText } from "../../utils/helper";
import { InputField } from "../InputField";

interface WorkspaceItemProps {
  workspace: Workspace;
  updateWorkspace: (w: Workspace) => void;
  projects: ProjectDetail[];
  starredProjects: ProjectDetail[];
  setStarredProjects: React.Dispatch<React.SetStateAction<ProjectDetail[]>>;
  updateWorkspaceProjects: (projects: ProjectDetail[]) => void;
  hasNextPage: boolean;
  fetchMoreProjects: (id: number) => void;
}

const WorkspaceItem: React.FC<WorkspaceItemProps> = ({
  workspace,
  updateWorkspace,
  projects,
  starredProjects,
  setStarredProjects,
  updateWorkspaceProjects,
  hasNextPage,
  fetchMoreProjects,
}) => {
  const navigate = useNavigate();
  const currentUser = getUser();

  const [showCreateProjectModal, setShowCreateProjectModal] =
    useState<boolean>(false);

  const [settingsAnchor, setSettingsAnchor] =
    React.useState<null | HTMLElement>(null);
  const [showRenameWorkspace, setShowRenameWorkspace] =
    useState<boolean>(false);
  const renameWorkspaceEl = useRef<HTMLInputElement | null>(null);

  const [membersAnchor, setMembersAnchor] = useState<HTMLElement | null>(null);
  const [members, setMembers] = useState<UserDetail[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredMembers = members.filter((m) =>
    getUserNameText(m).toLocaleLowerCase().includes(searchQuery.toLowerCase()),
  );

  useEffect(() => {
    if (showRenameWorkspace) {
      renameWorkspaceEl.current?.focus();
    }
  }, [showRenameWorkspace]);

  const createProjectformik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      const res = await createProject(values.name, workspace.id)
        .then((response) => {
          updateWorkspaceProjects([response, ...projects]);
        })
        .finally(() => {
          setShowCreateProjectModal(false);
        });
    },
  });

  const handleCreateProjectModal = () => {
    createProjectformik.resetForm();
    setShowCreateProjectModal(!showCreateProjectModal);
  };

  return (
    <div>
      <div className={styles.workspaceContainer}>
        <div className={styles.workspaceHeader}>
          <div className={styles.workspaceInfo}>
            <div className={styles.workspaceIconContainer}>
              <FolderIcon className={styles.workspaceIcon} />
              <h5>{getTextInitials(workspace.name)}</h5>
            </div>
            {showRenameWorkspace ? (
              <input
                type="text"
                className={styles.renameWorkspace}
                defaultValue={workspace.name}
                ref={renameWorkspaceEl}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                onBlur={(e) => {
                  if (e.relatedTarget?.classList.contains("MuiMenu-paper"))
                    return;
                  const newName = e.currentTarget.value.trim();
                  if (newName !== workspace.name && newName.length) {
                    renameWorkspace(workspace.id, newName).then((res) => {
                      updateWorkspace({
                        ...workspace,
                        name: res.name,
                      });
                    });
                  }
                  setShowRenameWorkspace(false);
                }}
              />
            ) : (
              <h3
                onClick={() => {
                  if (workspace.is_admin) {
                    setShowRenameWorkspace(true);
                  }
                }}
              >
                {workspace.name}
              </h3>
            )}

            <div className={styles.planTagContainer}>
              {workspace.plan === PLANS.FREE && (
                <div className={styles.freePlan}>FREE PLAN</div>
              )}

              {workspace.plan && workspace.plan !== PLANS.FREE && (
                <div className={styles.paidPlan}>
                  <CrownIcon className={styles.planCrownIcon} />
                  {workspace.plan}
                </div>
              )}
            </div>
          </div>
          <div className={styles.workspaceActions}>
            <div
              className={styles.workspaceUsers}
              // onMouseOver={(e) => {
              //   setMembersAnchor(null);
              //   setMembersAnchor(e.currentTarget);
              //   setMembers(workspace.users);
              // }}
              // onFocus={(e) => {
              //   setMembersAnchor(null);
              //   setMembersAnchor(e.currentTarget);
              //   setMembers(workspace.users);
              // }}
            >
              {workspace.users.slice(0, 3).map((u, index) =>
                u.profile_picture_url ? (
                  <img
                    key={u.id}
                    className={styles.workspaceUser}
                    src={u.profile_picture_url}
                    alt={u.profile_picture_url}
                    style={{
                      marginRight: "-10px",
                      zIndex: index,
                      border: "2px solid #27273A",
                    }}
                  />
                ) : (
                  <Avatar
                    key={u.id}
                    className={styles.workspaceUserAvatar}
                    {...{ children: getTextInitials(u.name) }}
                    sx={{ bgcolor: "purple", width: 32, height: 32 }}
                    style={{
                      marginRight: "-10px",
                      zIndex: index,
                      border: "2px solid #27273A",
                      textAlign: "center",
                    }}
                  />
                ),
              )}
              {workspace.users.length > 3 && (
                <div
                  className={styles.userCount}
                >{`+${workspace.users.length - 3}`}</div>
              )}
              <span>
                {workspace.users.map((m, index) => (
                  <React.Fragment key={index}>
                    {getUserNameText(m)}
                    <br />
                  </React.Fragment>
                ))}
              </span>
            </div>
            {workspace.is_admin && (
              <button
                type="button"
                className={styles.inviteUserButton}
                onClick={() => {
                  navigate(
                    `${SLAPSHOT_INVITE_USER}?workspaceId=${workspace?.id}`,
                  );
                }}
              >
                <AddUserIcon /> Share
              </button>
            )}

            <button
              type="button"
              className={styles.addProjectButton}
              onClick={handleCreateProjectModal}
            >
              <AddIcon /> Project
            </button>
            {workspace.is_admin && (
              <button
                className={styles.settingsButton}
                type="button"
                onClick={(e) => {
                  setSettingsAnchor(e.currentTarget);
                }}
              >
                <SettingsIcon />
              </button>
            )}
          </div>
        </div>
        {projects && projects.length > 0 ? (
          <>
            <div className={styles.workspaceProjects}>
              {projects.map((project) => (
                <ProjectItem
                  key={project.id}
                  workspace={workspace}
                  project={project}
                  starredProjects={starredProjects}
                  setStarredProjects={setStarredProjects}
                  updateProject={(prj) => {
                    updateWorkspaceProjects(
                      prj
                        ? projects.map((p) => (p.id === prj.id ? prj : p))
                        : projects.filter((p) => p.id !== project.id),
                    );
                  }}
                />
              ))}
            </div>
            {hasNextPage && (
              <div className={styles.loadMoreContainer}>
                <button
                  type="button"
                  className={styles.loadMoreButton}
                  onClick={() => fetchMoreProjects(workspace.id)}
                >
                  Load more projects
                  <ArrowDown />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.noProjectsContainer}>
            <div className={styles.text}>
              <FolderAddIcon />
              <h2>Create your first project</h2>
            </div>
            <Button
              className="primaryButton"
              onClick={handleCreateProjectModal}
            >
              <AddIcon className={styles.addIcon} /> Create New Project
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={showCreateProjectModal}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <FormikProvider value={createProjectformik}>
          <form
            className="modalContainer"
            onSubmit={createProjectformik.handleSubmit}
          >
            <div className="modalHeader">
              <h3 className="modalTitle">New Project</h3>
              <button
                onClick={handleCreateProjectModal}
                type="button"
                className="closeButton"
              >
                <XMark />
              </button>
            </div>
            <div className="modalContent">
              <p>
                Create project and upload all the shots here to create the
                predictions
              </p>
              <Field
                name="name"
                component={InputField}
                placeholder="Mission Impossible"
                label="Project Name"
              />
            </div>
            <div className="modalActions">
              <Button
                type="submit"
                disabled={
                  !!createProjectformik.errors.name ||
                  !createProjectformik.values.name
                }
                className="slapShotButton"
              >
                Create Project
              </Button>
            </div>
          </form>
        </FormikProvider>
      </Dialog>
      <Menu
        id="basic-menu"
        open={!!settingsAnchor}
        anchorEl={settingsAnchor}
        onClose={() => {
          setSettingsAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            marginTop: "4px",
            backgroundColor: "#111113",
            borderRadius: "7px",
            border: "1px solid #CCCCDC1F",
            padding: "8px 8px",
            width: "184px",
            boxShadow: "0px 8px 24px 0px #010409",
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          onClick={() => {
            setShowRenameWorkspace(true);
            setSettingsAnchor(null);
            renameWorkspaceEl.current?.focus();
          }}
        >
          <RenameIcon style={{ marginRight: "8px" }} />
          Rename
        </MenuItem>

        {workspace.is_admin && (
          <>
            <MenuItem
              className={styles.menuItem}
              onClick={() => {
                setSettingsAnchor(null);
                navigate(
                  `${SLAPSHOT_INVITE_USER}?workspaceId=${workspace?.id}`,
                );
              }}
            >
              <AddUserIcon style={{ marginRight: "8px" }} />
              Sharing settings
            </MenuItem>

            <MenuItem
              className={styles.menuItem}
              onClick={() => {
                if (workspace.creator.id === currentUser.id) {
                  navigate(SLAPSHOT_MY_PLAN_URL);
                } else {
                  navigate(
                    `${SLAPSHOT_SUBSCRIPTION_URL}?workspaceId=${workspace.id}&workspaceName=${workspace.name}&workspaceName=${workspace.name}`,
                  );
                }
              }}
            >
              <CrownIcon className={styles.crownIcon} />
              Manage Plan
            </MenuItem>
          </>
        )}
      </Menu>
      <Popover
        id="basic-menu"
        open={!!membersAnchor}
        anchorEl={membersAnchor}
        onClose={() => {
          setMembersAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            onMouseLeave: () => {
              setMembersAnchor(null);
            },
          },
        }}
        sx={{
          "& .MuiPaper-root": {
            maxHeight: "260px",
            marginTop: "4px",
            padding: "16px",
            backgroundColor: "#22242A",
            border: "1px solid #CCCCDC1F",
            borderRadius: "7px",
            boxShadow: "0px 8px 24px 0px #010409",
            overflowY: "hidden",
          },
        }}
      >
        <div className={styles.membersPopover}>
          <div className={styles.popoverHeader}>
            <p>Pro members:</p>
            <button
              type="button"
              onClick={() => {
                setMembersAnchor(null);
              }}
              aria-label="Close"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.66732 1.27398L8.72732 0.333984L5.00065 4.06065L1.27398 0.333984L0.333984 1.27398L4.06065 5.00065L0.333984 8.72732L1.27398 9.66732L5.00065 5.94065L8.72732 9.66732L9.66732 8.72732L5.94065 5.00065L9.66732 1.27398Z"
                  fill="white"
                  fillOpacity="0.6"
                />
              </svg>
            </button>
          </div>
          <div className={styles.members} />
        </div>
        <div className={styles.members}>
          {filteredMembers.map((m) => (
            <div key={m.id} className={styles.memberContainer}>
              {m.profile_picture_url ? (
                <img
                  src={m.profile_picture_url}
                  alt={m.profile_picture_url}
                  className={styles.memberProfile}
                />
              ) : (
                <Avatar
                  key={m.id}
                  className={styles.memberAvatar}
                  {...{ children: getTextInitials(m.name) }}
                  sx={{ bgcolor: "purple", width: 22, height: 22 }}
                />
              )}
              <h5>{getUserNameText(m)}</h5>
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
};

export default WorkspaceItem;
